import { AI_STUDIO_ADAPTERS, CUSTOM_BUCKET_ID } from '../../constants';
import mediaRuntimeCache from '../../ai-studio/utils/responsesCache';
import getMimeTypeByMediaType from '../../utils/getMimeTypeByMediaType';
import { getAttachmentSources, getOperationType, getParamRules, supportsAttachments } from './studioOperationRules';

const trimOrUndefined = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : value;
  return trimmed || undefined;
};

const getDropdownFieldValue = (field, fieldValues, dropdownOptions) => {
  const value = fieldValues[field];

  if (!dropdownOptions?.[field]) {
    return value;
  }

  const option = dropdownOptions[field].find((item) => item.value === value);
  return option?.value ?? value;
};

const getCachedMediaAsFile = (selectedMedia) => {
  const blob = mediaRuntimeCache.getBlob(selectedMedia.id);

  if (!blob) {
    throw new Error('Source file not found in cache');
  }

  const mimeType = selectedMedia.mimeType || getMimeTypeByMediaType(selectedMedia.mediaType);

  return new File([blob], selectedMedia.fileName, { type: mimeType });
};

const getStrategyAdapter = (adapter, bucket) => {
  return bucket?.bucketId === CUSTOM_BUCKET_ID ? AI_STUDIO_ADAPTERS.FILE : adapter;
};

const saveFilesAsRefs = async ({ files, onSaveFiles }) => {
  const validFiles = files.filter(Boolean);

  if (!validFiles.length) {
    return [];
  }

  return onSaveFiles({ files: validFiles });
};

const collectFilesBySources = ({ sources, file, attachments = [] }) => {
  const collected = [];

  if (sources.includes('file') && file) {
    collected.push(file);
  }

  if (sources.includes('attachments')) {
    collected.push(...attachments.map((attachment) => attachment?.file).filter(Boolean));
  }

  return collected;
};

const shouldSkipIfBlank = (rule, context) => {
  if (typeof rule.skipIfBlank === 'function') {
    return rule.skipIfBlank(context);
  }

  return Boolean(rule.skipIfBlank);
};

const applyParamRule = (rule, context) => {
  const { fieldValues, dropdownOptions } = context;

  if (rule.resolve) {
    return rule.resolve(context);
  }

  let value = fieldValues[rule.field];

  if (rule.dropdown) {
    value = getDropdownFieldValue(rule.field, fieldValues, dropdownOptions);
  }

  if (shouldSkipIfBlank(rule, context)) {
    return trimOrUndefined(value);
  }

  if (rule.transform) {
    return rule.transform(value, context);
  }

  return value;
};

const buildRequestParams = ({ operationType, mediaType, action, fieldValues, dropdownOptions }) => {
  const rules = getParamRules(operationType, mediaType);
  const context = { action, fieldValues, dropdownOptions, mediaType, operationType };

  return rules.reduce((params, rule) => {
    const value = applyParamRule(rule, context);

    if (value !== undefined) {
      params[rule.param] = value;
    }

    return params;
  }, {});
};

const buildStrategy = async ({ adapter, bucket, selectedMedia, onSaveFiles }) => {
  const strategyAdapter = getStrategyAdapter(adapter, bucket);

  if (strategyAdapter === AI_STUDIO_ADAPTERS.FILE) {
    const sourceFile = getCachedMediaAsFile(selectedMedia);
    const [fileId] = await saveFilesAsRefs({ files: [sourceFile], onSaveFiles });

    if (!fileId) {
      throw new Error('Failed to upload source file');
    }

    return { strategyAdapter, data: fileId };
  }

  return { strategyAdapter, data: selectedMedia.id };
};

const buildAttachedRefs = async ({ action, file, attachments, onSaveFiles }) => {
  const sources = getAttachmentSources(action);
  const files = collectFilesBySources({ sources, file, attachments });

  return saveFilesAsRefs({ files, onSaveFiles });
};

const buildStudioOperationRequest = async ({
  values,
  adapter,
  bucket,
  selectedMedia,
  mediaType,
  dropdownOptions,
  onSaveFiles,
}) => {
  const { action, message, attachments, file, ...restFieldValues } = values;
  const operationType = getOperationType(action);
  const fieldValues = { ...restFieldValues, message: message ?? '' };

  const strategy = await buildStrategy({ adapter, bucket, selectedMedia, onSaveFiles });

  const requestParams = buildRequestParams({
    operationType,
    mediaType,
    action,
    fieldValues,
    dropdownOptions,
  });

  const attachedRefs = supportsAttachments(action)
    ? await buildAttachedRefs({ action, file, attachments, onSaveFiles })
    : [];

  return {
    operationType,
    strategy,
    requestParams,
    ...(attachedRefs.length ? { attachedRefs } : {}),
  };
};

export {
  buildAttachedRefs,
  buildRequestParams,
  buildStrategy,
  buildStudioOperationRequest,
  collectFilesBySources,
  getCachedMediaAsFile,
  getDropdownFieldValue,
  getStrategyAdapter,
  saveFilesAsRefs,
  trimOrUndefined,
};
